/**
 * Alertas (alertas.html, F6): lo que el sistema quiere que el equipo mire
 * YA, con su porqué y su regla — «toda alerta lleva su porqué — o no se
 * envía». Silenciar DIFIERE hasta mañana (comportamiento local del
 * prototipo — no borra la causa); resolver ocurre en su propia pantalla
 * (cola, casas), aquí solo se navega. Sin sidepane (decisión del
 * contrato: la página no reserva columna de cola). Las 2 alertas y el
 * rastro de apagadas SON ALERTAS_SEED (alertas.seed.tsx) — el motor que
 * las detecta (SLA vencidos, casas sin actividad…) llega con F5b; hoy son
 * datos fijos. Estados: lista / vacío / cargando / error, forzables con
 * ?estado= (contrato del prototipo).
 */
import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Badge, Banner, Button, Card, CardHeader, Empty, Fainted, Feed, FeedItem, HitlCard,
  PageHead, Skeleton, Stale, useToast, type HitlAction,
} from "@skein/ui";
import { ALERTAS_SEED, APAGADAS_SEED, type AlertCard } from "./alertas.seed";

function SkeletonMesa() {
  const card = (kW: string, l2W: string) => (
    <Card>
      <Skeleton style={{ height: ".9em", width: kW, marginBottom: "var(--sk-sp-3)" }} />
      <Skeleton style={{ height: ".9em" }} />
      <Skeleton style={{ height: ".9em", width: l2W, marginTop: "var(--sk-sp-2)" }} />
    </Card>
  );
  return (
    <div className="mesa">
      {card("40%", "70%")}
      {card("50%", "60%")}
    </div>
  );
}

export function AlertasPage() {
  const say = useToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { estado?: string };
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [left, setLeft] = useState<Set<string>>(new Set());

  const silenciar = (c: AlertCard) => {
    setGone((g) => new Set(g).add(c.id));
    say(c.silenciaToast);
    window.setTimeout(() => setLeft((l) => new Set(l).add(c.id)), 280);
  };

  const vivas = ALERTAS_SEED.filter((c) => !left.has(c.id));
  const forced = search.estado;
  const view = forced === "vacio" || forced === "cargando" || forced === "error" ? forced : vivas.length === 0 ? "vacio" : "lista";

  const renderCard = (c: AlertCard, interactive = true) => {
    const actions: HitlAction[] = [
      { label: c.goLabel, variant: "go", onClick: () => void navigate({ to: c.goTo }) },
      { label: "Silenciar hasta mañana", onClick: () => silenciar(c) },
    ];
    return (
      <HitlCard
        key={c.id}
        kind={c.kindDanger ? <span style={{ color: "var(--sk-danger)" }}>{c.kind}</span> : c.kind}
        sla={c.sla}
        late={c.late}
        title={c.title}
        rows={c.rows}
        actions={actions}
        interactive={interactive}
        leaving={gone.has(c.id)}
      />
    );
  };

  return (
    <>
      <PageHead
        title="Alertas"
        meta={view === "vacio" ? undefined
          : view === "cargando" ? <Skeleton style={{ width: "10rem", height: "1.25em" }} />
          : <>
            <Badge tone="danger">{vivas.length === 1 ? "1 activa" : `${vivas.length} activas`}</Badge>
            <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>toda alerta lleva su porqué — o no se envía</Fainted>
          </>}
      />

      {view === "error" && (
        <Banner tone="danger" style={{ maxWidth: "46rem", marginBottom: "var(--sk-gap)" }}>
          <b>No llego a las alertas.</b> Lo que ves puede estar desactualizado. Reintento solo cada 30 s.{" "}
          <Button variant="link" onClick={() => say("Reintentando la conexión…")}>reintentar ahora</Button>
        </Banner>
      )}

      {view === "cargando" && <SkeletonMesa />}

      {view === "vacio" && (
        <Empty glyph="✳" title="El taller duerme tranquilo" style={{ maxWidth: "46rem" }}>
          Ninguna alerta activa. Y la regla de la casa sigue en pie: toda alerta lleva su porqué — o no se envía.
        </Empty>
      )}

      {(view === "lista" || view === "error") && (
        /* en error la copia queda congelada: inerte también a teclado (regla adjudicada en la Cola) */
        <Stale stale={view === "error"} className="mesa" inert={view === "error" || undefined}>
          {vivas.map((a) => renderCard(a, view === "lista"))}
          <Card flush aria-label="Apagadas esta semana">
            <CardHeader title="Apagadas esta semana" hint="se apagan solas al resolverse la causa" />
            <Feed full>
              {APAGADAS_SEED.map((a, i) => <FeedItem key={i} t={a.t}>{a.children}</FeedItem>)}
            </Feed>
          </Card>
        </Stale>
      )}
    </>
  );
}
